import React, { useEffect, useMemo, useRef, useState } from "react";
import { useVoiceRecorder } from "./VoiceRecorder";

/**
 * Клик по кнопке:
 *  - если НЕ пишем → старт, input заменяется панелью (● время  ОТМЕНА)
 *  - если пишем    → стоп и ОТПРАВКА
 * Кнопка во время записи = «Отправить» (иконка ➤) + эквалайзер, пульсирующий по уровню.
 */
export default function VoiceRecordButton({
  onFinished,
  deviceId = "",
  maxMs = 5 * 60 * 1000,
  onRecordUIReplace,   // (node) => заменить input
  onRecordUIRestore,   // () => вернуть input
}) {
  const [level, setLevel] = useState(0);
  const silentCancelRef = useRef(false);

  const rec = useVoiceRecorder({
    maxMs,
    deviceId,
    onDevices: () => { },
    onLevel: (v) => setLevel(v),
    onStop: ({ blob, mime, durationMs }) => {
      if (silentCancelRef.current) { silentCancelRef.current = false; return; }
      if (blob && blob.size) onFinished?.({ blob, mime, durationMs });
    },
  });

  // m:ss,д (десятые через запятую — как в TG)
  const timeStr = useMemo(() => {
    const ms = rec.elapsedMs || 0;
    const t = Math.floor(ms / 1000);
    const m = Math.floor(t / 60);
    const s = String(t % 60).padStart(2, "0");
    const d = Math.floor((ms % 1000) / 100);
    return `${m}:${s},${d}`;
  }, [rec.elapsedMs]);

  // эквалайзер: 5 столбиков, высота от громкости с лёгким джиттером
  const eq = useMemo(() => {
    const n = 5;
    const base = Math.min(1, Math.max(0, level));
    return Array.from({ length: n }, (_, i) => {
      const rnd = 0.6 + 0.4 * Math.sin((Date.now() / 90 + i) % (2 * Math.PI)); // плавный джиттер
      const h = 6 + Math.round(22 * base * rnd); // 6..28px
      return h;
    });
  }, [level]);

  // пульсация кольца: быстрее и сильнее при громкости
  const pulseMs = Math.max(260, 900 - level * 600); // 900ms..260ms
  const scale = 1 + level * 0.35;
  const glow = (0.25 + level * 0.45).toFixed(3);

  // показать/убрать панель замены инпута
  useEffect(() => {
    if (rec.recording) {
      onRecordUIReplace?.(
        <div className="tgbar">
          <span className="tgbar__dot" />
          <span className="tgbar__time">{timeStr}</span>
          <button
            type="button"
            className="tgbar__cancel"
            onClick={() => {
              silentCancelRef.current = true;
              rec.stop();          // стоп без отправки
              onRecordUIRestore?.();
            }}
          >
            ОТМЕНА
          </button>
        </div>
      );
    } else {
      onRecordUIRestore?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rec.recording, timeStr]);

  const handleClick = async () => {
    if (!rec.recording) {
      await rec.start();  // старт записи
      return;
    }
    // запись идёт → отправляем
    rec.stop();
  };

  return (
    <div className="vr">
      <button
        type="button"
        className={`vr__btn ${rec.recording ? "vr__btn--send" : "vr__btn--mic"}`}
        onClick={handleClick}
        title={rec.recording ? "Отправить голосовое" : "Записать голосовое"}
        style={{
          // переменные для анимаций
          "--pulse-ms": `${pulseMs}ms`,
          transform: rec.recording ? `scale(${scale})` : "scale(1)",
          boxShadow: rec.recording ? `0 0 0 10px rgba(37,99,235,${glow})` : "none",
        }}
      >
        {/* Эквалайзер (виден только во время записи) */}
        {rec.recording && (
          <div className="vr__eq" aria-hidden>
            {eq.map((h, i) => (
              <span key={i} className="vr__eq-bar" style={{ height: h }} />
            ))}
          </div>
        )}

        {/* Иконка */}
        <span className="vr__icon">
          {rec.recording ? "➤" : "🎤"}
        </span>
      </button>
    </div>
  );
}
